import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import "card-pattern/dist/main.css";
import ScrollToTopBtn from "../../../components/common/ScrollToTopBtn";
import Loader from "../../../components/common/Loader";
import BreadCrumbs from "../../../components/common/BreadCrumbs/BreadCrumbs";
import type { BreadcrumbItem } from "../../../components/common/BreadCrumbs";
import Table, {
  TableColumn,
  TableRow,
} from "../../../components/common/Table/Table";
import CardTable from "../../../components/common/CardTable";
import { useNavigate, useLocation , Link } from "react-router-dom";
import Dropdown, { DropdownOption } from "../../../components/common/Dropdown";
import { ValidationError } from "../../../components/common/ErrorBanner";
import CommonErrorBanner from "../../../components/common/CommonErrorBanner";
import UploadDocumentSection from "./UploadDocumentSection";
import DeleteModal from "../ProjectDetails/ProjectHelper/DeleteModal";
import formatDate from "../../../utils/formatDate";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  saveSelectedProjectId,
  loadSelectedProjectId,
} from "../../../utils/teamDocumentsSessionStorage";
import { teamStorage } from "../../../utils/teamStorage";
import { useProfile } from "../../../contexts/ProfileContext";
import {
  fetchTeamData,
  fetchTeamProjects,
  fetchProjectDetails,
  fetchProjectDocuments,
  fetchAllProjectDocuments,
  fetchTeamLevelDocuments,
  downloadDocument,
  uploadDocument,
  deleteDocument,
  resetTeamDocuments,
  clearDownloadError,
  clearUploadState,
  clearDeleteState,
} from "../../../store/slices/teamDocumentSlice";
import PopUp from "../../../components/common/PopUp/PopUp";

// Columns for Selected Project Documents table (first table)
const selectedProjectInfoColumns: TableColumn[] = [
  { key: "selectedProjectName", label: "Selected Project Name" },
  { key: "projectId", label: "Project ID" },
  { key: "startDate", label: "Start Date" },
  { key: "endDate", label: "End Date" },
  { key: "status", label: "Status" },
];

// Columns for Selected Project Documents table (second table with documents)
const selectedProjectDocsColumns: TableColumn[] = [
  { key: "deleteIcon", label: "Delete" },
  { key: "documentName", label: "Document Name" },
  { key: "documentId", label: "Document ID" },
  { key: "savedBy", label: "Saved By" },
  { key: "savedByTeamRole", label: "Saved By Team Role" },
  { key: "dateSaved", label: "Date Saved" },
];

// Columns for UBT Team Level Documents
const teamLevelDocsColumns: TableColumn[] = [
  { key: "documentName", label: "Document Name" },
  { key: "savedBy", label: "Saved By" },
  { key: "savedByTeamRole", label: "Saved By Team Role" },
  { key: "dateSaved", label: "Date Saved" },
];

// Columns for Project Documents (All Projects)
const allProjectDocsColumns: TableColumn[] = [
  { key: "projectName", label: "Project Name" },
  { key: "projectId", label: "Project ID" },
  { key: "documentName", label: "Document Name" },
  { key: "savedBy", label: "Saved By" },
  { key: "savedByTeamRole", label: "Saved By Team Role" },
  { key: "dateSaved", label: "Date Saved" },
];

// Helper functions to reduce cognitive complexity

interface AccessPermissions {
  canDelete: boolean;
  canUpload: boolean;
  canUploadToTeamLevel: boolean;
  canView: boolean;
}

/**
 * Finds the team role for a specific team from team memberships
 */
const getTeamMembershipRole = (
  teamMemberships: any[] | undefined,
  teamId: string,
): string | null => {
  if (!teamMemberships || teamMemberships.length === 0) {
    return null;
  }

  const membership = teamMemberships.find(
    (m) => String(m.teamId) === String(teamId),
  );

  return membership?.teamRole ?? null;
};

/**
 * Determines access permissions based on primaryRole and teamRole
 * Priority: primaryRole "Administrator" → full access
 * Otherwise: check teamRole for the specific team
 */
const getAccessPermissions = (
  primaryRole: string | undefined,
  teamRole: string | null,
): AccessPermissions => {
  // 1. Administrator
  if (primaryRole === "Administrator") {
    return {
      canDelete: true,
      canUpload: true,
      canUploadToTeamLevel: true,
      canView: true,
    };
  }

  // 2. No team role or explicit view-only -> read-only
  if (!teamRole || teamRole === "View-Only") {
    return {
      canDelete: false,
      canUpload: false,
      canUploadToTeamLevel: false,
      canView: true,
    };
  }

  // 3. Team-level administrators and consultants -> full access
  if (teamRole === "Administrator" || teamRole === "Consultant") {
    return {
      canDelete: true,
      canUpload: true,
      canUploadToTeamLevel: true,
      canView: true,
    };
  }

  // 4. TH/WPS Champion -> project-level uploads only
  if (teamRole === "TH/WPS Champion") {
    return {
      canDelete: false,
      canUpload: true,
      canUploadToTeamLevel: false,
      canView: true,
    };
  }

  // 5. Other upload-capable roles
  const uploadRoles = [
    "Co-Lead",
    "Labor Co-Lead",
    "Sponsor",
    "Labor Sponsor",
    "Proxy",
  ];

  if (uploadRoles.includes(teamRole)) {
    return {
      canDelete: false,
      canUpload: true,
      canUploadToTeamLevel: true,
      canView: true,
    };
  }

  // 6. Default: view-only
  return {
    canDelete: false,
    canUpload: false,
    canUploadToTeamLevel: false,
    canView: true,
  };
};

/**
 * Validates file size and format
 */
const validateFile = (
  file: File,
): { valid: boolean; sizeError?: string; formatError?: string } => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedExtensions = [
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".csv",
    ".ppt",
    ".pptx",
    ".pdf",
    ".txt",
    ".bmp",
    ".gif",
    ".jpg",
    ".png",
  ];

  if (file.size > maxSize) {
    return {
      valid: false,
      sizeError: "File size greater than 10MB is not allowed.",
    };
  }

  const fileExtension = file.name
    .substring(file.name.lastIndexOf("."))
    .toLowerCase();
  if (!allowedExtensions.includes(fileExtension)) {
    return {
      valid: false,
      formatError:
        "Invalid file type. Only Office Files and Image files are allowed.",
    };
  }

  return { valid: true };
};

/**
 * Determines upload type and typeId based on selected project
 */
const getUploadParams = (
  selectedProject: DropdownOption | null,
  teamId: string,
): { type: "team" | "project"; typeId: string | number } => {
  const isTeamLevel =
    !selectedProject || String(selectedProject.value).startsWith("team-");
  return {
    type: isTeamLevel ? "team" : "project",
    typeId: isTeamLevel ? teamId : selectedProject.value,
  };
};

/**
 * Generates table rows for selected project documents
 */
const generateSelectedProjectRows = (
  docs: any[],
  handleDelete: (id: number) => void,
): TableRow[] => {
  return docs.map((doc: any) => ({
    rowKey: `selected-doc-${doc.documentId}`,
    deleteIcon: (
      <button
        type="button"
        onClick={() => handleDelete(doc.documentId)}
        className="button -light cursor-pointer shrink-0 px-4! py-2!"
        aria-label="Delete"
      >
        <img
          src="/assets/images/trash-solid-blue.svg"
          alt="Delete icon"
          className="min-w-8 min-h-8"
          width={24}
          height={24}
          style={{ display: "block" }}
        />
        <span>Delete</span>
      </button>
    ),
    documentId: doc.documentId,
    documentName: doc.documentName,
    savedBy: doc.savedBy,
    savedByTeamRole: doc.savedByTeamRole,
    dateSaved: formatDate(doc.dateSaved, "MM-DD-YYYY"),
    projectId: doc.projectId,
  }));
};

/**
 * Determines which project to select based on session storage or defaults to first option
 * Uses stored projectId if available, otherwise defaults to first option
 */
const selectInitialProject = (options: DropdownOption[]): DropdownOption => {
  const storedProjectId = loadSelectedProjectId();

  // Use stored projectId if available and found in options
  if (storedProjectId !== null) {
    const storedProject = options.find(
      (opt) => opt.value === String(storedProjectId),
    );
    if (storedProject) {
      return storedProject;
    }
  }

  // Default to first option (when no stored data or project not found)
  return options[0];
};

/**
 * Fetches project documents and details for a given project
 */
const fetchProjectData = (
  dispatch: any,
  teamId: string,
  projectId: string | number,
  nuid: string | null,
): void => {
  dispatch(
    fetchProjectDocuments({
      teamId,
      projectId,
      nuid,
    }),
  );
  dispatch(
    fetchProjectDetails({
      projectId,
      nuid,
    }),
  );
};

/**
 * Determines download parameters (type and typeId) based on row data
 */
const getDownloadParams = (
  rowData: any,
  selectedTeamId: string,
  selectedProject: DropdownOption | null,
): { type: "team" | "project"; typeId: number | string } => {
  // For team level documents, use team type
  if (rowData.isTeamLevel) {
    return { type: "team", typeId: selectedTeamId };
  }

  // For project documents with projectId, use project type
  if (rowData.projectId) {
    return { type: "project", typeId: rowData.projectId };
  }

  // Fallback to selected project
  return { type: "project", typeId: selectedProject?.value ?? selectedTeamId };
};

/**
 * Triggers browser download for a blob
 */
const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

/**
 * Generates team level document table rows
 */
/**
 * Generates team level document table rows with mapped savedByTeamRole
 * @param docs - Array of document objects
 * @param profile - Current user profile
 * @param selectedTeamId - Currently selected team ID
 * @returns Array of TableRow objects
 */

/**
 * Determines the savedByTeamRole for a document and profile context.
 * @param doc - Document object
 * @param profile - Current user profile
 * @param selectedTeamId - Currently selected team ID
 * @returns The resolved team role string
 */
export const resolveSavedByTeamRole = (
  doc: any,
  profile: any,
  selectedTeamId: string | null
): string => {
  let savedByTeamRole = doc.savedByTeamRole ?? "";
  // Only apply the team role logic if profile.nuid matches savedBy
  if (
    profile &&
    Array.isArray(profile.teamMemberships) &&
    selectedTeamId &&
    profile.nuid &&
    doc.savedBy &&
    String(profile.nuid) === String(doc.savedBy)
  ) {
    const membership = profile.teamMemberships.find(
      (m: any) => String(m.teamId) === String(selectedTeamId)
    );
    if (membership?.teamRole) {
      savedByTeamRole = membership.teamRole;
    } else {
      savedByTeamRole = profile?.primaryRole ?? "";
    }
  }
  return savedByTeamRole;
};

const generateTeamLevelRows = (
  docs: any[],
): TableRow[] => {
  return docs.map((doc: any) => ({
    rowKey: `team-doc-${doc.documentId}`,
    documentName: doc.documentName,
    savedBy: doc.savedBy,
    savedByTeamRole: doc.savedByTeamRole,
    dateSaved: formatDate(doc.dateSaved, "MM-DD-YYYY"),
    documentId: doc.documentId,
    isTeamLevel: true,
  }));
};

/**
 * Generates all project documents table rows
 */
/**
 * Generates all project documents table rows with mapped savedByTeamRole
 * @param docs - Array of document objects
 * @param profile - Current user profile
 * @param selectedTeamId - Currently selected team ID
 * @returns Array of TableRow objects
 */
const generateAllProjectRows = (
  docs: any[],
): TableRow[] => {
  return docs.map((doc: any) => ({
    rowKey: `all-doc-${doc.documentId}`,
    projectName: doc.projectName,
    projectId: doc.projectId,
    documentName: doc.documentName,
    savedBy: doc.savedBy,
    savedByTeamRole: doc.savedByTeamRole,
    dateSaved: formatDate(doc.dateSaved, "MM-DD-YYYY"),
    documentId: doc.documentId,
  }));
};

/**
 * Generates project info table rows for selected project
 */
const generateProjectInfoRows = (
  selectedProject: DropdownOption | null,
  isTeamLevelSelected: boolean,
  projects: any[],
  selectedProjectDetails: any,
): TableRow[] => {
  if (
    !selectedProject ||
    isTeamLevelSelected ||
    projects.length === 0 ||
    !selectedProjectDetails
  ) {
    return [];
  }

  return [
    {
      rowKey: "project-info-1",
      selectedProjectName: selectedProject.label ?? "",
      projectId: selectedProject.value ?? "",
      startDate: selectedProjectDetails.perfStartDate
        ? formatDate(selectedProjectDetails.perfStartDate, "MM-DD-YYYY")
        : "",
      endDate: selectedProjectDetails.perfEndDate
        ? formatDate(selectedProjectDetails.perfEndDate, "MM-DD-YYYY")
        : "",
      status: selectedProjectDetails.perfStatus ?? "",
    },
  ];
};

/**
 * Processes file selection and validation
 */
const processFileSelection = (
  file: File,
  setFileSizeError: (error: string) => void,
  setFileFormatError: (error: string) => void,
  setValidationErrors: (errors: ValidationError[]) => void,
  setSelectedFile: (file: File | null) => void,
): void => {
  setFileSizeError("");
  setFileFormatError("");
  setValidationErrors([]);

  const validation = validateFile(file);

  if (!validation.valid) {
    if (validation.sizeError) {
      setFileSizeError(validation.sizeError);
    }
    if (validation.formatError) {
      setFileFormatError(validation.formatError);
    }
    setSelectedFile(null);
    return;
  }

  setSelectedFile(file);
};

/**
 * Validates file name for special characters
 */
const validateFileName = (fileName: string): ValidationError[] => {
  const errors: ValidationError[] = [];
  const specialCharsRegex = /[+]/;

  if (specialCharsRegex.test(fileName)) {
    errors.push({
      field: "fileName",
      message: `Special Characters in File Name are not allowed.(${fileName})`,
    });
  }

  return errors;
};

/**
 * Handles project change actions including saving to session and fetching data
 */
const handleProjectChangeActions = (
  option: DropdownOption,
  selectedTeamId: string | null,
  dispatch: any,
  nuid: string | null,
): void => {
  // Save projectId to session storage for non-team-level projects
  if (!String(option.value).startsWith("team-") && selectedTeamId) {
    saveSelectedProjectId(option.value);
  }

  // Fetch documents for project-level selections
  if (!String(option.value).startsWith("team-") && selectedTeamId) {
    fetchProjectData(dispatch, selectedTeamId, option.value, nuid);
  }
};

/**
 * Refreshes all document tables (all projects, team level, and selected project)
 */
const refreshAllDocumentTables = (
  dispatch: any,
  selectedTeamId: string,
  selectedProject: DropdownOption | null,
  nuid: string | null,
): void => {
  dispatch(fetchAllProjectDocuments({ teamId: selectedTeamId, nuid }));
  dispatch(fetchTeamLevelDocuments({ teamId: selectedTeamId, nuid }));

  if (selectedProject && !String(selectedProject.value).startsWith("team-")) {
    dispatch(
      fetchProjectDocuments({
        teamId: selectedTeamId,
        projectId: selectedProject.value,
        nuid,
      }),
    );
  }
};

/**
 * Auto-selects initial project and fetches its data
 */
const autoSelectAndFetchProject = (
  options: DropdownOption[],
  dispatch: any,
  selectedTeamId: string,
  nuid: string | null,
  setSelectedProject: (project: DropdownOption) => void,
): void => {
  const projectToSelect = selectInitialProject(options);
  setSelectedProject(projectToSelect);

  // Save selected project to session storage
  if (!String(projectToSelect.value).startsWith("team-")) {
    saveSelectedProjectId(projectToSelect.value);
  }

  // Fetch documents and details for selected project (skip team-level)
  if (!String(projectToSelect.value).startsWith("team-")) {
    fetchProjectData(dispatch, selectedTeamId, projectToSelect.value, nuid);
  }
};

/**
 * Loads initial data when team is selected
 */
const loadInitialTeamData = (
  dispatch: any,
  selectedTeamId: string,
  nuid: string | null,
): void => {
  dispatch(resetTeamDocuments());
  dispatch(fetchTeamData({ teamId: selectedTeamId, nuid }));
  dispatch(fetchTeamProjects({ teamId: selectedTeamId, nuid }));
  dispatch(fetchAllProjectDocuments({ teamId: selectedTeamId, nuid }));
  dispatch(fetchTeamLevelDocuments({ teamId: selectedTeamId, nuid }));
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const TeamDocuments: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const stateTeamId = (location.state as { teamId?: string | number } | null)
    ?.teamId;
  const selectedTeamId: string | null =
    stateTeamId == null
      ? teamStorage.getTeamId()
      : String(stateTeamId);
  const { profile } = useProfile();

  // Redux state
  const {
    teamData,
    projects,
    selectedProjectDetails,
    selectedProjectDocuments,
    selectedProjectDocumentsLoading,
    allProjectDocuments,
    allProjectDocumentsLoading,
    teamLevelDocuments,
    teamLevelDocumentsLoading,
    downloadError,
    uploadLoading,
    uploadError,
    uploadSuccess,
    uploadMessage,
    deleteError,
    deleteSuccess,
    deleteMessage,
    pageLoading,
  } = useAppSelector((state) => state.teamDocumentSlice);

  // Calculate access permissions based on primaryRole and teamRole
  const teamRole = selectedTeamId
    ? getTeamMembershipRole(profile?.teamMemberships, selectedTeamId)
    : null;
  const permissions = getAccessPermissions(profile?.primaryRole, teamRole);
  const { canUpload, canDelete, canUploadToTeamLevel } = permissions;

  // Local state
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const [selectedProject, setSelectedProject] = useState<DropdownOption | null>(
    null,
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileSizeError, setFileSizeError] = useState<string>("");
  const [fileFormatError, setFileFormatError] = useState<string>("");
  const [projectOptions, setProjectOptions] = useState<DropdownOption[]>([]);

  // Unified banner state - only one banner shown at a time
  const [banner, setBanner] = useState<{
    show: boolean;
    variant: "Success" | "Error" | "Alert";
    heading: string;
    message: string;
  } | null>(null);

  const [uploadFormKey, setUploadFormKey] = useState<number>(0); // Key to force reset of upload form
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [documentToDelete, setDocumentToDelete] = useState<number | null>(null);
  const bannerTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousTeamIdRef = useRef<string | null>(null); // Track previous teamId for change detection
  const previousProjectsLengthRef = useRef<number>(0); // Track if projects list changed from empty to populated

  /**
   * Shows a banner and auto-dismisses after 10 seconds
   * Automatically clears any existing banner before showing new one
   */
  const showBanner = useCallback(
    (
      variant: "Success" | "Error" | "Alert",
      heading: string,
      message: string,
    ) => {
      // Clear existing timeout
      if (bannerTimeoutRef.current) {
        clearTimeout(bannerTimeoutRef.current);
      }

      // Show new banner
      setBanner({ show: true, variant, heading, message });

      // Auto-dismiss after 10 seconds
      bannerTimeoutRef.current = setTimeout(() => {
        setBanner(null);
      }, 10000);
    },
    [],
  );

  /**
   * Clears the current banner
   */
  const clearBanner = useCallback(() => {
    if (bannerTimeoutRef.current) {
      clearTimeout(bannerTimeoutRef.current);
    }
    setBanner(null);
  }, []);

  // Enable Save button only when valid file is selected
  const isSaveEnabled = Boolean(
    selectedFile && !fileSizeError && !fileFormatError,
  );

  // Watch for Redux state changes and show banners accordingly
  useEffect(() => {
    if (uploadSuccess && uploadMessage) {
      showBanner("Success", "Upload Successful", uploadMessage);
      dispatch(clearUploadState());
    } else if (uploadError) {
      showBanner("Error", "Upload Failed", uploadError);
      dispatch(clearUploadState());
    }
  }, [uploadSuccess, uploadError, uploadMessage, showBanner, dispatch]);

  useEffect(() => {
    if (deleteSuccess && deleteMessage) {
      showBanner("Success", "Delete Successful", deleteMessage);
      dispatch(clearDeleteState());
    } else if (deleteError) {
      showBanner("Error", "Delete Failed", deleteError);
      dispatch(clearDeleteState());
    }
  }, [deleteSuccess, deleteError, deleteMessage, showBanner, dispatch]);

  useEffect(() => {
    if (downloadError) {
      showBanner("Error", "Download Error", downloadError);
      dispatch(clearDownloadError());
    }
  }, [downloadError, showBanner, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (bannerTimeoutRef.current) clearTimeout(bannerTimeoutRef.current);
    };
  }, []);

  // Helper to refresh all document tables
  const refreshDocumentTables = useCallback(() => {
    if (!selectedTeamId) return;

    const nuid = profile?.nuid ? String(profile.nuid) : null;
    refreshAllDocumentTables(dispatch, selectedTeamId, selectedProject, nuid);
  }, [selectedTeamId, selectedProject, dispatch, profile?.nuid]);

  // Refresh tables after successful upload or delete
  useEffect(() => {
    if ((uploadSuccess || deleteSuccess) && selectedTeamId) {
      // Add a small delay to ensure backend has processed the change
      setTimeout(() => {
        refreshDocumentTables();
      }, 100);
    }
  }, [uploadSuccess, deleteSuccess, selectedTeamId, refreshDocumentTables]);

  // Fetch all data on page load when teamId is available
  // Re-fetches data on navigation (location.pathname change) to ensure fresh data
  useEffect(() => {
    if (!selectedTeamId) return;

    // Only reset selectedProject when teamId actually changes (not on page refresh)
    const teamIdChanged =
      previousTeamIdRef.current !== null &&
      previousTeamIdRef.current !== selectedTeamId;
    if (teamIdChanged) {
      setSelectedProject(null);
      previousProjectsLengthRef.current = 0; // Reset projects length tracker on team change
    }

    // Track current teamId (nullish coalescing for first load)
    previousTeamIdRef.current ??= selectedTeamId;
    if (teamIdChanged) {
      previousTeamIdRef.current = selectedTeamId;
    }

    const nuid = profile?.nuid ? String(profile.nuid) : null;
    loadInitialTeamData(dispatch, selectedTeamId, nuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamId, dispatch, profile?.nuid, location.pathname]);

  // Build project options from fetched projects when projects list is loaded
  useEffect(() => {
    if (!selectedTeamId) return;

    // If no projects exist, clear options and selection
    if (projects.length === 0) {
      setProjectOptions([]);
      setSelectedProject(null);
      previousProjectsLengthRef.current = 0;
      return;
    }

    // Projects exist - build options list
    const options: DropdownOption[] = projects.map((project) => ({
      id: project.perfId,
      label: project.projectName,
      value: String(project.perfId),
    }));

    // Add team-level upload option only if user has permission
    if (canUploadToTeamLevel) {
      options.push({
        id: `team-${selectedTeamId}`,
        label: `${selectedTeamId} (upload to team level)`,
        value: `team-${selectedTeamId}`,
      });
    }

    setProjectOptions(options);

    // Auto-select when:
    // 1. No project is currently selected (initial load or team change), OR
    // 2. Selected project doesn't exist in new options (team changed), OR
    // 3. Projects just loaded (changed from 0 to >0) and current selection is team-level
    const selectedProjectExists =
      selectedProject &&
      options.some((opt) => opt.value === selectedProject.value);
    const projectsJustLoaded =
      previousProjectsLengthRef.current === 0 && projects.length > 0;
    const isCurrentlyTeamLevel =
      selectedProject?.value.startsWith("team-") ?? false;

    if (
      !selectedProject ||
      !selectedProjectExists ||
      (projectsJustLoaded && isCurrentlyTeamLevel)
    ) {
      const nuid = profile?.nuid ? String(profile.nuid) : null;
      autoSelectAndFetchProject(
        options,
        dispatch,
        selectedTeamId,
        nuid,
        setSelectedProject,
      );
    }

    // Update previous projects length
    previousProjectsLengthRef.current = projects.length;
  }, [projects, selectedTeamId, dispatch, profile?.nuid, canUploadToTeamLevel]);

  // No need to navigate based on session storage since teamId is in URL params
  // Session storage only stores projectId for convenience

  /**
   * Build breadcrumb items for navigation
   * UBT Home → [Team Name] Projects → Team Documents
   */
  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    const items: BreadcrumbItem[] = [
      {
        label: "UBT Home",
        onClick: () => navigate("/home"),
      },
    ];

    // Add team link if teamId and teamName are available
    if (selectedTeamId && teamData?.teamName) {
      items.push({
        label: `Project: ${teamData.teamName}`,
        path: `/home/projects`,
        state: { teamId: selectedTeamId, from: "Team Documents" },
        title: `Project: ${teamData.teamName}`, // Full name for tooltip in case of long names
      });
    }

    // Add current page
    items.push({
      label: "Team Documents",
    });

    return items;
  }, [selectedTeamId, teamData?.teamName, navigate]);

  const handleProjectChange = useCallback(
    (option: DropdownOption): void => {
      setSelectedProject(option);
      setValidationErrors([]);
      // Note: Not clearing selectedFile to preserve user's file selection when switching projects

      // Clear banner when project changes
      clearBanner();

      // Handle session storage and data fetching
      const nuid = profile?.nuid ? String(profile.nuid) : null;
      handleProjectChangeActions(option, selectedTeamId, dispatch, nuid);
    },
    [dispatch, selectedTeamId, profile?.nuid, clearBanner],
  );

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0];
    if (file) {
      processFileSelection(
        file,
        setFileSizeError,
        setFileFormatError,
        setValidationErrors,
        setSelectedFile,
      );
    }
  };

  const handleCancel = (): void => {
    setSelectedFile(null);
    setValidationErrors([]);
    setFileSizeError("");
    setFileFormatError("");
    setUploadFormKey((prev) => prev + 1); // Force reset of upload form component
  };

  const validateFileUpload = (): boolean => {
    if (!selectedFile) {
      return true; // No file selected, no validation needed
    }

    const errors = validateFileName(selectedFile.name);
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSaveDocument = async (): Promise<void> => {
    if (!validateFileUpload() || !selectedFile || !selectedTeamId || !profile) {
      return;
    }

    // Validate filename length (max 80 characters)
    if (selectedFile.name.length > 80) {
      showBanner(
        "Error",
        "Invalid Document Name",
        "The document name should not be beyond 80 characters",
      );
      return;
    }

    try {
      const nuid = profile.nuid;
      const createdBy = nuid;

      // Determine type and typeId using helper function
      const { type, typeId } = getUploadParams(selectedProject, selectedTeamId);

      // Reuse resolveSavedByTeamRole for savedByTeamRole payload
      const createdByRole = resolveSavedByTeamRole(
        { savedBy: nuid },
        profile,
        selectedTeamId
      );

      await dispatch(
        uploadDocument({
          type,
          typeId,
          file: selectedFile,
          createdBy,
          createdByRole,
          nuid,
        }),
      ).unwrap();

      // Clear form after successful upload
      handleCancel();
    } catch (error) {
      console.error("Failed to upload document:", error);
    }
  };

  const handleDownloadDocument = useCallback(
    async (rowData: any) => {
      if (!selectedTeamId) return;

      try {
        const nuid = profile?.nuid ? String(profile.nuid) : null;
        const documentId = rowData.documentId;

        // Determine type and typeId based on the source table
        const { type, typeId } = getDownloadParams(
          rowData,
          selectedTeamId,
          selectedProject,
        );

        const result = await dispatch(
          downloadDocument({ type, typeId, documentId, nuid }),
        ).unwrap();

        // Create blob and download
        const blob = new Blob([result], { type: "application/octet-stream" });
        triggerBlobDownload(blob, rowData.documentName ?? "document");

        // Clear any download errors
        dispatch(clearDownloadError());
      } catch (error) {
        console.error("Failed to download document:", error);
      }
    },
    [dispatch, selectedTeamId, selectedProject, profile?.nuid],
  );

  const handleDeleteDocument = (documentId: number): void => {
    setDocumentToDelete(documentId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!documentToDelete) return;

    try {
      const nuid = profile?.nuid ? String(profile.nuid) : null;

      await dispatch(
        deleteDocument({
          documentId: documentToDelete,
          nuid,
        }),
      ).unwrap();

      setDeleteModalOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error("Failed to delete document:", error);
      setDeleteModalOpen(false);
      setDocumentToDelete(null);
    }
  };

  const handleCancelDelete = (): void => {
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  // Determine if team-level is selected
  const isTeamLevelSelected =
    selectedProject?.value.startsWith("team-") ?? false;

  // Generate table rows
  const selectedProjectInfoRows: TableRow[] = generateProjectInfoRows(
    selectedProject,
    isTeamLevelSelected,
    projects,
    selectedProjectDetails,
  );

  const selectedProjectDocsRows: TableRow[] = generateSelectedProjectRows(
    selectedProjectDocuments,
    handleDeleteDocument,
  );

  // Conditionally include Delete column only when user can delete and there are documents
  const selectedProjectDocsColumnsWithDelete: TableColumn[] =
    canDelete && selectedProjectDocsRows.length > 0
      ? selectedProjectDocsColumns
      : selectedProjectDocsColumns.filter((col) => col.key !== "deleteIcon");

  const teamLevelDocsRows: TableRow[] =
    generateTeamLevelRows(teamLevelDocuments);

  const allProjectDocsRows: TableRow[] =
    generateAllProjectRows(allProjectDocuments);

  return (
    <div className="min-h-[70vh] lg:mx-40! mx-5!">
      <div className="flex justify-between items-end ">
        <div className="mb-6!">
          <BreadCrumbs items={breadcrumbItems} />
        </div>

        {selectedTeamId && (
          <div className="mobile-margin lg:mt-15!">
            <div className="about-this-team" style={{ color: '#677083' }}> About this team :</div>
            <Link
              to="/home/path-to-performance"
              state={{ teamId: selectedTeamId, from: "Team Documents" }}
              data-analytics-type="link"
              className="no-underline! p1 -medium"
            >
              P2P
            </Link>
            <Link
              to="/home/projects"
              state={{ teamId: selectedTeamId, from: "Team Documents" }}
              data-analytics-type="link"
              className="no-underline! p1 -medium pl-6! "
            >
              Projects
            </Link>
            <Link
              to="/home/history"
              state={{ teamId: selectedTeamId, from: "Team Documents" }}
              data-analytics-type="link"
              className="no-underline! p1 -medium pl-6!"
            >
              History
            </Link>
          </div>
        )}
      </div>

      {/* Section 1: Team Documents heading */}
      <div className="desktop:mt-6! md:mt-6! mobile:mt-3.5!">
        <h1 className="h1 my-0!">Team Documents</h1>
      </div>

      {/* Warning banner when no teamId is present */}
      {!selectedTeamId && (
        <CommonErrorBanner
          variant="Alert"
          heading="Please select an UBT"
          messageInfo="Please select an UBT before accessing Path to Performance (P2P) page OR Team Documents page. You can access the page from Select UBT/ My Teams/ Team Details page etc.<br> <li> <a href='/home/select-ubt'> Select a UBT </a> </li>"
        />
      )}

      {/* Warning banner when no projects exist for selected team */}
      {selectedTeamId && projects.length === 0 && !pageLoading && (
        <CommonErrorBanner
          variant="Alert"
          heading=""
          messageInfo="No Projects exist for the UBT(Unit Based Team) selected."
        />
      )}

      {/* Only show content if teamId is present and projects exist */}
      {selectedTeamId && projects.length > 0 && (
        <>
          {/* Section 2: Team name heading */}
          <div className="mt-6!">
            <h2 className="h2 my-0! tablet:mt-8! mobile:mt-3.5! w-full max-w-full whitespace-normal break-all [overflow-wrap:anywhere]">
              {teamData?.teamName ?? `Team ${selectedTeamId}`}
            </h2>
          </div>
          <div className="mx-5!">
            {/* Section 3: Project Selection - Visible to all users */}
            <div className="mt-4!">
              <div className="px-5">
                <div className="flex flex-col desktop:flex-row gap-15 w-[80%]!">
                  <div className="flex-1">
                    <p className="p3 -book my-0!">
                      Documents can be uploaded to a project or can be uploaded
                      to the team level by selecting the UBT ID# in the drop
                      down
                    </p>
                    <div
                      className="ds-form mb-4 mt-8!"
                      data-ds-theme="vessel"
                      data-ds-variant="input"
                      data-ds-version="1"
                    >
                    
                      <Dropdown
                        label={
                          <span className="flex items-center gap-1">
                            Select a Project
                            <PopUp
                              className="-medium"
                              message="<p>Documents can be uploaded to a project or can be uploaded to the team level by selecting the UBT ID# in the drop down</p>"
                            />
                          </span>
                        }
                        selectedValue={
                          selectedProject ?? {
                            id: 0,
                            label: "Select a Project",
                            value: "0",
                          }
                        }
                        options={projectOptions}
                        onSelect={handleProjectChange}
                        placeholder="Select a Project"
                        buttonClassName="w-[338px]! min-w-[338px]!"
                        menuClassName="max-h-[480px] overflow-y-auto! w-[515px]! min-w-[515px]!"
                      />

                    </div>
                  </div>

                  {/* File Upload Controls - Only visible to Administrator, Consultant, Co-Lead */}
                  {canUpload && (
                    <UploadDocumentSection
                      key={uploadFormKey}
                      selectedFile={selectedFile}
                      fileFormatError={fileFormatError}
                      fileSizeError={fileSizeError}
                      isSaveEnabled={isSaveEnabled}
                      uploadLoading={uploadLoading}
                      validationErrors={validationErrors}
                      onFileChange={handleFileChange}
                      onSaveDocument={handleSaveDocument}
                      onCancel={handleCancel}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Unified Banner - shows one banner at a time, visible for both project and team-level uploads */}
            {banner && banner.show && (
              <div className="mt-15! mb-4">
                <CommonErrorBanner
                  variant={banner.variant}
                  heading={banner.heading}
                  messageInfo={banner.message}
                />
              </div>
            )}

            {/* Section 4: Selected Project Documents (60px gap) */}
            {/* Hide this section when team-level upload option is selected */}
            {selectedProject && !isTeamLevelSelected && (
              <div className="mt-15!">

                <p className="p1 -medium mb-4">Selected Project Documents</p>

                {/* First table: Project info - only show if rows exist */}
                {selectedProjectInfoRows.length > 0 && (
                  <CardTable className="mb-6">
                    <Table
                      columns={selectedProjectInfoColumns}
                      rows={selectedProjectInfoRows}
                      className="mt-6! [&>thead>tr]:border-t-0!"
                    />
                  </CardTable>
                )}

                {/* Second table: Project documents */}
                <div className="pt-6">
                  {(() => {
                    if (selectedProjectDocumentsLoading) {
                      return (
                        <div className="flex justify-center items-center min-h-100">
                          <Loader />
                        </div>
                      );
                    }

                    if (selectedProjectDocsRows.length > 0) {
                      return (
                        <CardTable>
                          <Table
                            columns={selectedProjectDocsColumnsWithDelete}
                            rows={selectedProjectDocsRows}
                            actionKey="documentName"
                            onActionClick={handleDownloadDocument}
                            className="mt-6! [&>thead>tr]:border-t-0!"
                          />
                        </CardTable>
                      );
                    }

                    return (
                      <p className="p3 -book text-center mt-[84px]!">
                        <b>No Documents saved for the Selected Project.</b>
                      </p>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Section 5: UBT Team Level Documents (60px gap) */}
            <div className="mt-15!">
              <p className="p1 -medium mb-4">
                UBT Team Level Documents &nbsp;
                <PopUp message="<p>Documents uploaded here are not specific to any project.</p>" />
              </p>
              <p className="p3 -book mb-4">
                Documents uploaded here are not specific to any project
              </p>

              {teamLevelDocumentsLoading ? (
                <div className="flex justify-center items-center min-h-100">
                  <Loader />
                </div>
              ) : (
                <CardTable>
                  {teamLevelDocsRows.length > 0 ? (
                    <Table
                      columns={teamLevelDocsColumns}
                      rows={teamLevelDocsRows}
                      actionKey="documentName"
                      onActionClick={handleDownloadDocument}
                      className="[&>thead>tr]:border-t-0"
                    />
                  ) : (
                    <p className="p3 -book text-center mt-[84px]!">
                      <b>No team level documents found.</b>
                    </p>
                  )}
                </CardTable>
              )}
            </div>

            {/* Section 6: Project Documents (All Projects) */}
            <div className="mt-15!">
              <p className="p1 -medium mb-4">
                Project Documents (All Projects) &nbsp;
                <PopUp message="<p>Documents appearing are the most recent project documentation for all projects within the UBT</p>" />
              </p>
              <p className="p3 -book mb-4">
                Documents appearing are the most recent project documentation
                for all projects within the UBT
              </p>

              {allProjectDocumentsLoading ? (
                <div className="flex justify-center items-center min-h-100">
                  <Loader />
                </div>
              ) : (
                <CardTable>
                  {allProjectDocsRows.length > 0 ? (
                    <Table
                      columns={allProjectDocsColumns}
                      rows={allProjectDocsRows}
                      actionKey="documentName"
                      onActionClick={handleDownloadDocument}
                      className="[&>thead>tr]:border-t-0"
                    />
                  ) : (
                    <p className="p3 -book text-center mt-[84px]!">
                      <b>No project documents found.</b>
                    </p>
                  )}
                </CardTable>
              )}
            </div>

            {/* Scroll to Top Button */}
            <div className="mb-10! mt-10!">
              <div className="-mb-27! tablet:hidden">
                <ScrollToTopBtn />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Page loader for initial data loading */}
      {pageLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-75 flex justify-center items-center z-50">
          <Loader />
        </div>
      )}

      {/* Download error banner */}
      {downloadError && (
        <div className="mt-4">
          <CommonErrorBanner
            variant="Error"
            heading="Download Failed"
            messageInfo={downloadError}
          />
        </div>
      )}

      {/* Delete confirmation modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        title="Delete Document"
        deleteInfo="Are you sure you want to delete this document?"
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default TeamDocuments;
